// Tutorial Tree Sidebar - Hierarchical page navigation
// Collapsible tree with search, drag & drop, and context actions
import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ChevronRight, ChevronDown, Plus, Search, FileText,
  MoreHorizontal, Copy, Trash2, Eye, EyeOff, GripVertical,
  FolderOpen, ChevronsUpDown, ChevronsDownUp, ArrowLeft, Library,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import useTutorialStore from '@/stores/tutorialStore';

export default function TutorialTreeSidebar({ userId, currentPageId, onSelectPage }) {
  const {
    pages, searchQuery, setSearchQuery, expandedNodes, toggleNode,
    expandAll, collapseAll, deletePage, duplicatePage, toggleStatus,
    toggleVisibility, createPage, expandNodes,
  } = useTutorialStore();

  const navigate = useNavigate();
  const [dragOverId, setDragOverId] = useState(null);

  const pageById = useMemo(() => new Map(pages.map(p => [p.id, p])), [pages]);

  // Chaîne d'ancêtres de la page ouverte, de la racine (tutoriel) à elle-même
  const ancestors = useMemo(() => {
    const chain = [];
    let node = pageById.get(currentPageId);
    const seen = new Set();
    while (node && !seen.has(node.id)) {
      seen.add(node.id);
      chain.unshift(node.id);
      node = node.parent_id ? pageById.get(node.parent_id) : null;
    }
    return chain;
  }, [currentPageId, pageById]);

  const rootId = ancestors[0] || null;
  const rootPage = rootId ? pageById.get(rootId) : null;

  // Un tutoriel = un espace : on ne garde que sa page principale et ses descendants
  const scopedPages = useMemo(() => {
    if (!rootId) return pages;
    const keep = new Set([rootId]);
    let added = true;
    while (added) {
      added = false;
      for (const p of pages) {
        if (p.parent_id && keep.has(p.parent_id) && !keep.has(p.id)) {
          keep.add(p.id);
          added = true;
        }
      }
    }
    return pages.filter(p => keep.has(p.id));
  }, [pages, rootId]);

  // La branche de la page ouverte est dépliée automatiquement
  useEffect(() => {
    if (ancestors.length > 1) expandNodes(ancestors.slice(0, -1));
  }, [ancestors, expandNodes]);

  // Build tree from flat list
  const buildTree = useCallback((items, parentId = null) => {
    return items
      .filter(p => p.parent_id === parentId)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(page => ({
        ...page,
        children: buildTree(items, page.id),
      }));
  }, []);

  // Filter pages by search (dans l'espace du tutoriel courant uniquement)
  const filteredPages = searchQuery
    ? scopedPages.filter(p =>
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.preview || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : scopedPages;

  const tree = searchQuery ? filteredPages.map(p => ({ ...p, children: [] })) : buildTree(filteredPages);

  const handleCreatePage = async (parentId = null) => {
    const page = await createPage({
      title: 'Nouvelle page',
      parent_id: parentId,
      order: pages.filter(p => p.parent_id === parentId).length,
    }, userId);
    if (page) {
      navigate(`/tutorials/${page.id}`);
    }
  };

  const handleSelect = (page) => {
    if (onSelectPage) {
      onSelectPage(page);
    } else {
      navigate(`/tutorials/${page.id}`);
    }
  };

  return (
    <div className="tut-tree-sidebar" data-testid="tutorial-tree-sidebar">
      {/* Retour au catalogue : la sidebar ne montre que le tutoriel courant */}
      <button type="button" className="tut-tree-back" onClick={() => navigate('/tutorials')}>
        <ArrowLeft size={12} />
        <Library size={12} />
        <span>Tous les tutoriels</span>
      </button>

      {/* Header */}
      <div className="tut-tree-header">
        <h3 className="tut-tree-scope" title={rootPage?.title || 'Pages'}>
          <span className="tut-tree-scope-icon">{rootPage?.icon || <FileText size={13} />}</span>
          <span className="tut-tree-scope-title">{rootPage?.title || 'Pages'}</span>
        </h3>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            variant="ghost" size="icon" className="h-6 w-6"
            onClick={() => expandAll()}
            title="Tout déplier"
          >
            <ChevronsUpDown size={12} />
          </Button>
          <Button
            variant="ghost" size="icon" className="h-6 w-6"
            onClick={() => collapseAll()}
            title="Tout replier"
          >
            <ChevronsDownUp size={12} />
          </Button>
          <Button
            variant="ghost" size="icon" className="h-6 w-6"
            onClick={() => handleCreatePage(rootId)}
            title={rootId ? 'Nouvelle sous-page de ce tutoriel' : 'Nouveau tutoriel'}
          >
            <Plus size={12} />
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="tut-tree-search">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher..."
            className="pl-8 h-7 text-xs"
          />
        </div>
      </div>

      {/* Tree */}
      <div className="tut-tree-content">
        {tree.length === 0 ? (
          <div className="tut-tree-empty">
            <FileText size={24} className="text-muted-foreground/50 mb-2" />
            <p className="text-xs text-muted-foreground">
              {searchQuery ? 'Aucun résultat' : 'Aucun tutoriel'}
            </p>
            {!searchQuery && (
              <Button
                variant="outline" size="sm" className="mt-2 text-xs h-7"
                onClick={() => handleCreatePage()}
              >
                <Plus size={12} className="mr-1" /> Créer
              </Button>
            )}
          </div>
        ) : (
          tree.map(node => (
            <TreeNode
              key={node.id}
              node={node}
              depth={0}
              rootId={rootId}
              currentPageId={currentPageId}
              expandedNodes={expandedNodes}
              toggleNode={toggleNode}
              onSelect={handleSelect}
              onCreateChild={(parentId) => handleCreatePage(parentId)}
              onDelete={(id) => deletePage(id, userId)}
              onDuplicate={(id) => duplicatePage(id, userId)}
              onToggleStatus={(id) => toggleStatus(id, userId)}
              onToggleVisibility={(id) => toggleVisibility(id, userId)}
              dragOverId={dragOverId}
              setDragOverId={setDragOverId}
            />
          ))
        )}
      </div>
    </div>
  );
}

function TreeNode({
  node, depth, rootId, currentPageId, expandedNodes, toggleNode,
  onSelect, onCreateChild, onDelete, onDuplicate,
  onToggleStatus, onToggleVisibility,
  dragOverId, setDragOverId,
}) {
  const isExpanded = expandedNodes[node.id];
  const isActive = node.id === currentPageId;
  const hasChildren = node.children && node.children.length > 0;
  const isRoot = node.id === rootId;

  return (
    <div className="tut-tree-node-wrapper">
      <div
        className={`tut-tree-node ${isActive ? 'tut-tree-node-active' : ''} ${isRoot ? 'tut-tree-node-root' : ''} ${dragOverId === node.id ? 'tut-tree-node-dragover' : ''}`}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={() => onSelect(node)}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData('text/plain', node.id);
          e.dataTransfer.effectAllowed = 'move';
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setDragOverId(node.id);
        }}
        onDragLeave={() => setDragOverId(null)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOverId(null);
          // Drag & drop reorder handled by parent
        }}
        data-testid={`tree-node-${node.id}`}
      >
        {/* Expand/Collapse */}
        <button
          type="button"
          className="tut-tree-toggle"
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) toggleNode(node.id);
          }}
        >
          {hasChildren ? (
            isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />
          ) : (
            <span className="w-3" />
          )}
        </button>

        {/* Icon */}
        <span className="tut-tree-icon">
          {node.icon || (hasChildren ? '📁' : '📄')}
        </span>

        {/* Title */}
        <span className="tut-tree-title" title={node.title}>
          {node.title}
        </span>

        {/* Status badges */}
        <div className="tut-tree-badges">
          {isRoot && (
            <Badge variant="outline" className="text-[9px] py-0 px-1 h-4 tut-tree-badge-root">Principale</Badge>
          )}
          {node.status === 'draft' && (
            <Badge variant="secondary" className="text-[9px] py-0 px-1 h-4">Brouillon</Badge>
          )}
          {node.is_visible && node.status === 'published' && (
            <Eye size={10} className="text-emerald-500" />
          )}
        </div>

        {/* Context Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <button className="tut-tree-menu-btn" type="button">
              <MoreHorizontal size={12} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onCreateChild(node.id); }}>
              <Plus size={14} className="mr-2" /> Sous-page
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDuplicate(node.id); }}>
              <Copy size={14} className="mr-2" /> Dupliquer
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onToggleStatus(node.id); }}>
              <FileText size={14} className="mr-2" />
              {node.status === 'published' ? 'Passer en brouillon' : 'Publier'}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onToggleVisibility(node.id); }}>
              {node.is_visible ? <EyeOff size={14} className="mr-2" /> : <Eye size={14} className="mr-2" />}
              {node.is_visible ? 'Masquer aux candidats' : 'Rendre visible'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={(e) => { e.stopPropagation(); onDelete(node.id); }}
            >
              <Trash2 size={14} className="mr-2" /> Supprimer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div className="tut-tree-children">
          {node.children.map(child => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              rootId={rootId}
              currentPageId={currentPageId}
              expandedNodes={expandedNodes}
              toggleNode={toggleNode}
              onSelect={onSelect}
              onCreateChild={onCreateChild}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              onToggleStatus={onToggleStatus}
              onToggleVisibility={onToggleVisibility}
              dragOverId={dragOverId}
              setDragOverId={setDragOverId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
